<template>
  <div>
    <v-simple-table
      v-if="files.length"
      class="table--narrow mb-2"
    >
      <template v-slot:default>
        <thead>
          <tr>
            <th />
            <th>{{ $t('FileUploader.Filename') }}</th>
            <th />
            <th
              v-if="selectEntity"
              style="width: 40%;"
            >
              {{ $t('Outbox.ReceiveDept') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="file in files"
            :key="file.id"
          >
            <td>
              <v-btn
                text
                icon
                x-small
                color="red"
                @click.prevent="$refs.upload.remove(file)"
              >
                <v-icon>{{ mdiMinusCircleOutline }}</v-icon>
              </v-btn>
            </td>
            <td>
              <div class="filename">
                {{ file.name }}
              </div>
              <div
                v-if="file.active || file.progress !== '0.00'"
              >
                <v-progress-linear
                  :value="file.progress"
                  :color="file.error ? 'red' : 'primary'"
                />
              </div>
            </td>
            <td>
              <v-icon
                v-if="file.success"
                color="primary"
              >
                {{ mdiCheckCircle }}
              </v-icon>
            </td>
            <td v-if="selectEntity">
              <v-select
                v-model="file.response.entityId"
                filled
                :items="entitiesWithAll"
                :item-text="item => item.abbr +' - '+ $chPtObjData('chinName', item)"
                item-value="id"
                :label="$t('Outbox.ReceiveDept')"
                solo
                dense
                hide-details
                class="font-small"
              />
            </td>
          </tr>
        </tbody>
      </template>
    </v-simple-table>

    <v-sheet
      v-show="showNoFileBox"
      v-else
      color="grey lighten-3 grey--text"
      max-width="600"
      class="pa-3"
    >
      {{ $t('FileUploader.NoFile') }}
    </v-sheet>

    <v-btn
      outlined
      color="grey darken-2"
      class="mt-3"
      @click="onAddFile"
    >
      {{ label }}
    </v-btn>

    <file-upload
      ref="upload"
      v-model="files"
      :input-id="inputId"
      :post-action="postUrl"
      :extensions="extensions"
      :accept="accept"
      :multiple="multiple"
      :size="size || 0"
      :drop="drop"
      :headers="{'Authorization': `Bearer ${this.$g2eOauth.token}`}"
      :maximum="maximum || 0"
      @input-filter="inputFilter"
      @input-file="inputFile"
      @input="inputUpdate"
    />
  </div>
</template>

<style scoped>
  .table--narrow th,
  .table--narrow td {
    padding-left: 8px;
    padding-right: 8px;
  }
  .font-small {
    font-size: 14px;
  }
</style>

<script>

import FileUpload from 'vue-upload-component'
import clientLogApi from '@/api/general/clientLog'
import CryptoUtilFile from '@/lib/g2e/CryptoUtilFile.js'
import { mdiMinusCircleOutline, mdiCheckCircle } from '@mdi/js'

export default {
  components: {
    FileUpload
  },
  filters: {
    formatSize: function (val) {
      return Math.round(val / 1024) + 'KB'
    }
  },
  props: {
    label: {
      type: String,
      required: true
    },
    postUrl: {
      type: String,
      required: true
    },
    extensions: {
      type: String,
      default: 'pdf'
    },
    accept: {
      type: String,
      default: 'application/pdf'
    },
    multiple: {
      type: Boolean,
      default: false
    },
    size: {
      type: Number,
      default: 0
    },
    maximum: {
      type: Number,
      default: 0
    },
    drop: {
      type: Boolean,
      default: true
    },
    thread: {
      type: Number,
      default: 3
    },
    uploadAuto: {
      type: Boolean,
      default: true
    },
    selectEntity: {
      type: Boolean,
      default: false
    },
    showNoFileBox: {
      type: Boolean,
      default: true
    },
    filename: {
      type: String,
      default: null
    },
    response: {
      type: Array,
      default: null
    },
    entities: {
      type: Array,
      default: null
    },
    filePassword: {
      type: String,
      default: null
    }
  },
  data: function () {
    return {
      inputId: null,
      files: [],
      mdiMinusCircleOutline: mdiMinusCircleOutline,
      mdiCheckCircle: mdiCheckCircle
    }
  },
  computed: {
    successResponse () {
      let resps = []
      this.files.forEach(item => {
        if (item.success) {
          resps.push({ name: item.name, response: item.response })
        }
      })
      return resps
    },
    entitiesWithAll () {
      return [{
        id: null,
        abbr: 'ALL',
        chinName: '所有',
        portName: 'Todos'
      }, ...this.entities]
    }
  },
  watch: {
    files: function (val) {
      // console.log('files change')
      // console.log(this.files)
      // console.log(this.files.length)
      this.$emit('update:response', this.successResponse)
    }
  },
  mounted () {
    this.inputId = 'file-uploader_' + this._uid
  },
  methods: {
    inputFilter (newFile, oldFile, prevent) {
      if (newFile && !oldFile) {
        // Before adding a file
        // 添加文件前
        // Filter system files or hide files
        // 过滤系统文件 和隐藏文件
        if (/(\/|^)(Thumbs\.db|desktop\.ini|\..+)$/.test(newFile.name)) {
          return prevent()
        }
        // Filter php html js file
        // 过滤 php html js 文件
        if (/\.(php5?|html?|jsx?)$/i.test(newFile.name)) {
          return prevent()
        }
      }

      // Encrypt file
      // 加密文件
      // 如果 newFile 值为 undefined 则是删除
      // 如果 oldFile 值为 undefined 则是添加
      // 如果 newFile, oldFile 都存在则是更新
      if (newFile && newFile.file && newFile.error === '' && !newFile.name.endsWith('.encrypted')) {
        newFile.error = 'encrypting'
        // console.log(this.filePassword)
        CryptoUtilFile.encryptFile(newFile.file, this.filePassword)
          .then((file) => {
            this.$refs.upload.update(newFile, { error: '', file, name: newFile.name + '.encrypted', size: file.size, type: file.type })
          })
          .catch((err) => {
            this.$refs.upload.update(newFile, { error: err.message || 'encrypt' })
          })
      }
    },
    inputFile (newFile, oldFile) {
      if (newFile && !oldFile) {
        // add
        this.$emit('update:filename', newFile.file.name)
      }
      if (newFile && oldFile) {
        // update
        // console.log('update', newFile)

        // Upload error
        if (newFile.error !== oldFile.error && oldFile.error !== 'encrypting') {
          // console.log('error', newFile.error, newFile)
          let errLog = {
            'responseData': JSON.stringify(newFile.response),
            'responseConfigData': JSON.stringify({
              name: newFile.file.name,
              size: newFile.file.size,
              type: newFile.file.type
            }),
            'url': newFile.postAction,
            'status': newFile.xhr.status,
            'statusText': newFile.xhr.statusText
          }
          clientLogApi.submitHttpErrorStatus(errLog, cb => {})
          this.$emit('uploadError', newFile)
        }

        // 上传成功
        if (newFile.success !== oldFile.success) {
          // console.log('success', newFile.success, newFile.response.uploadFileId)
          // console.log(this.files)
          this.$emit('uploadSuccess')
        }
      }
      if (!newFile && oldFile) {
        // remove
        // console.log('remove', oldFile)
      }

      // Automatically activate upload
      if (Boolean(newFile) !== Boolean(oldFile) || oldFile.error !== newFile.error) {
        if (this.uploadAuto && !this.$refs.upload.active) {
          this.$refs.upload.active = true
        }
      }
    },
    onAddFile () {
      let label = this.$refs.upload.$el.querySelector('label')
      label.click()
    },
    inputUpdate (files) {
      // console.log('inputUpdate')
      // console.log(files)
    },
    clearFile () {
      this.files = []
    },
    setFile (file) {
      this.files = file
    }
    // toBase64 (file) {
    //   return new Promise((resolve, reject) => {
    //     const reader = new FileReader()
    //     reader.readAsDataURL(file)
    //     reader.onload = () => resolve(reader.result)
    //     reader.onerror = error => reject(error)
    //   })
    // },
  }
}
</script>
